/** Shared number formatters for Nanom Wallet. */

const NG_PER_GRAM = 1e9;

/** Strip trailing zeros after decimal (and trailing dot). */
export function stripZeros(s: string): string {
  if (!s.includes(".")) return s;
  return s.replace(/\.?0+$/, "");
}

/** Balance / quantity — no worthless trailing zeros; prefer integer when whole. */
export function fmtAmount(v: unknown, opts?: { maxFrac?: number; integer?: boolean }): string {
  if (v == null || v === "") return "—";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  if (opts?.integer || Number.isInteger(n) || Math.abs(n - Math.round(n)) < 1e-9) {
    return String(Math.round(n));
  }
  const max = opts?.maxFrac ?? 8;
  return stripZeros(n.toFixed(max));
}

/**
 * USD display. Oracle metals arrive as usd_per_nanogram — convert to USD/g first.
 * Never dump scientific notation for normal UI.
 */
export function fmtUsd(v: unknown): string {
  if (v == null || v === "") return "—";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  if (n === 0) return "0";
  const abs = Math.abs(n);
  if (abs >= 1000) return stripZeros(n.toLocaleString("en-US", { maximumFractionDigits: 2 }));
  if (abs >= 1) return stripZeros(n.toFixed(4));
  if (abs >= 0.01) return stripZeros(n.toFixed(4));
  if (abs >= 1e-4) return stripZeros(n.toFixed(6));
  return stripZeros(n.toFixed(8));
}

/** Convert oracle nanogram USD → display USD (per gram). */
export function toDisplayUsd(
  raw: unknown,
  unit?: string | null
): number | null {
  const n = Number(String(raw ?? "").replace(/,/g, ""));
  if (!Number.isFinite(n)) return null;
  if (unit === "usd_per_nanogram") return n * NG_PER_GRAM;
  return n;
}

const METAL_BASES = new Set(["NAG", "NAU", "NPT", "NPD", "NSN", "NNI", "NCU", "NPB"]);

export function isMetalBase(sym: string): boolean {
  return METAL_BASES.has(sym.toUpperCase());
}

/** Spot/book prices: nano-USD → USD/g when value looks like nanogram quote. */
export function normalizeSpotPrice(raw: unknown, pairOrBase?: string): number | null {
  const n = Number(String(raw ?? "").replace(/,/g, ""));
  if (!Number.isFinite(n)) return null;
  const base = (pairOrBase || "").includes("_")
    ? (pairOrBase || "").slice(0, (pairOrBase || "").indexOf("_")).toUpperCase()
    : (pairOrBase || "").toUpperCase();
  if (isMetalBase(base) && Math.abs(n) > 0 && Math.abs(n) < 1e-4) {
    return n * NG_PER_GRAM;
  }
  return n;
}

export function fmtSpotPrice(raw: unknown, pairOrBase?: string): string {
  const n = normalizeSpotPrice(raw, pairOrBase);
  if (n == null) return raw != null && raw !== "" ? String(raw) : "—";
  return fmtUsd(n);
}

export const SPOT_PAIR_HINT_KEY = "nanom.spot.pairHint";
